<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component — MarkdownToolbar
  // ------------------------------------------------------------
  // 게시글 작성 textarea 위에 마운트되는 서식 툴바.
  // 굵게/기울임/취소선/제목/리스트/인용/코드/링크는 Markdown 문법을 삽입하고,
  // 글자 크기·색상은 화이트리스트 inline style 을 가진 <span>을 삽입한다.
  // 서버측 sanitizer(MarkdownRenderer)가 위험한 CSS를 자동 필터링한다.
  // ============================================================

  type Props = {
    value: string
    textareaId: string
    onUpdate: (next: string) => void
  }

  let { value, textareaId, onUpdate }: Props = $props()

  let sizeOpen = $state(false)
  let colorOpen = $state(false)

  const SIZES: Array<{ label: string; value: string }> = [
    { label: '12', value: '12px' },
    { label: '14', value: '14px' },
    { label: '16', value: '16px' },
    { label: '18', value: '18px' },
    { label: '20', value: '20px' },
    { label: '24', value: '24px' },
    { label: '32', value: '32px' },
  ]

  const COLORS: string[] = [
    '#0000FF', // primary
    '#263238', // secondary
    '#111827', // text
    '#6B7280', // muted
    '#EF4444', // red
    '#F59E0B', // amber
    '#10B981', // green
    '#3B82F6', // blue
    '#8B5CF6', // violet
    '#EC4899', // pink
  ]

  function getTextarea(): HTMLTextAreaElement | null {
    if (typeof document === 'undefined') return null
    return document.getElementById(textareaId) as HTMLTextAreaElement | null
  }

  function wrapSelection(before: string, after: string, placeholder: string) {
    const ta = getTextarea()
    if (!ta) return
    const start = ta.selectionStart ?? value.length
    const end = ta.selectionEnd ?? value.length
    const selected = value.slice(start, end)
    const middle = selected || placeholder
    const next = value.slice(0, start) + before + middle + after + value.slice(end)
    onUpdate(next)
    queueMicrotask(() => {
      ta.focus()
      const selStart = start + before.length
      const selEnd = selStart + middle.length
      ta.setSelectionRange(selStart, selEnd)
    })
  }

  function insertLinePrefix(prefix: string, placeholder = '') {
    const ta = getTextarea()
    if (!ta) return
    const start = ta.selectionStart ?? value.length
    const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1
    const lineEnd = value.indexOf('\n', start)
    const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd)
    const stripped = currentLine.replace(/^(#{1,6}\s|>\s|[-*]\s|\d+\.\s)/, '')
    const replaced = (stripped || placeholder)
    const next = value.slice(0, lineStart) + prefix + replaced + value.slice(lineStart + currentLine.length)
    onUpdate(next)
    queueMicrotask(() => {
      ta.focus()
      const cursor = lineStart + prefix.length + replaced.length
      ta.setSelectionRange(lineStart + prefix.length, cursor)
    })
  }

  function insertLink() {
    if (typeof window === 'undefined') return
    const url = window.prompt('링크 URL을 입력하세요', 'https://')
    if (!url) return
    wrapSelection('[', `](${url})`, '링크 텍스트')
  }

  function applySize(size: string) {
    sizeOpen = false
    wrapSelection(`<span style="font-size: ${size}">`, '</span>', '텍스트')
  }

  function applyColor(color: string) {
    colorOpen = false
    wrapSelection(`<span style="color: ${color}">`, '</span>', '텍스트')
  }

  function closeMenus() {
    sizeOpen = false
    colorOpen = false
  }
</script>

<svelte:window onclick={closeMenus} />

<div
  class="flex flex-wrap items-center gap-1 px-2 py-2 mb-2 bg-surface-tertiary border border-default rounded-lg"
  role="toolbar"
  aria-label="텍스트 서식 도구"
>
  <!-- Inline -->
  <button type="button" class="tb-btn font-bold" title="굵게 (Ctrl+B)"
    onclick={() => wrapSelection('**', '**', '굵은 텍스트')}>B</button>
  <button type="button" class="tb-btn italic" title="기울임 (Ctrl+I)"
    onclick={() => wrapSelection('*', '*', '기울임 텍스트')}>I</button>
  <button type="button" class="tb-btn line-through" title="취소선"
    onclick={() => wrapSelection('~~', '~~', '취소선 텍스트')}>S</button>
  <button type="button" class="tb-btn" title="인라인 코드"
    onclick={() => wrapSelection('`', '`', '코드')}>{'</>'}</button>

  <span class="tb-sep" aria-hidden="true"></span>

  <!-- Headings -->
  <button type="button" class="tb-btn" title="제목 1"
    onclick={() => insertLinePrefix('# ', '제목')}>H1</button>
  <button type="button" class="tb-btn" title="제목 2"
    onclick={() => insertLinePrefix('## ', '제목')}>H2</button>
  <button type="button" class="tb-btn" title="제목 3"
    onclick={() => insertLinePrefix('### ', '제목')}>H3</button>

  <span class="tb-sep" aria-hidden="true"></span>

  <!-- Lists / Blocks -->
  <button type="button" class="tb-btn" title="순서 없는 목록"
    onclick={() => insertLinePrefix('- ', '항목')}>•</button>
  <button type="button" class="tb-btn" title="순서 있는 목록"
    onclick={() => insertLinePrefix('1. ', '항목')}>1.</button>
  <button type="button" class="tb-btn" title="인용"
    onclick={() => insertLinePrefix('> ', '인용문')}>"</button>

  <span class="tb-sep" aria-hidden="true"></span>

  <!-- Link -->
  <button type="button" class="tb-btn" title="링크" onclick={insertLink}>🔗</button>

  <span class="tb-sep" aria-hidden="true"></span>

  <!-- Font size -->
  <div class="relative">
    <button type="button" class="tb-btn flex items-center gap-1" title="글자 크기"
      onclick={(e) => { e.stopPropagation(); colorOpen = false; sizeOpen = !sizeOpen }}>
      <span>크기</span>
      <svg class="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M3 4.5l3 3 3-3"/>
      </svg>
    </button>
    {#if sizeOpen}
      <div
        role="menu"
        tabindex="-1"
        class="absolute z-20 mt-1 left-0 min-w-[120px] bg-surface border border-default rounded-lg shadow-md py-1"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => { if (e.key === 'Escape') sizeOpen = false }}
      >
        {#each SIZES as size (size.value)}
          <button type="button" role="menuitem"
            class="w-full text-left px-3 py-1.5 text-body2 text-content hover:bg-surface-tertiary transition-colors"
            style={`font-size: ${size.value}`}
            onclick={() => applySize(size.value)}>
            {size.label}px
          </button>
        {/each}
      </div>
    {/if}
  </div>

  <!-- Color -->
  <div class="relative">
    <button type="button" class="tb-btn flex items-center gap-1" title="글자 색상"
      onclick={(e) => { e.stopPropagation(); sizeOpen = false; colorOpen = !colorOpen }}>
      <span>색상</span>
      <svg class="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M3 4.5l3 3 3-3"/>
      </svg>
    </button>
    {#if colorOpen}
      <div
        role="menu"
        tabindex="-1"
        class="absolute z-20 mt-1 left-0 bg-surface border border-default rounded-lg shadow-md p-2 grid grid-cols-5 gap-1"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => { if (e.key === 'Escape') colorOpen = false }}
      >
        {#each COLORS as color (color)}
          <button type="button" role="menuitem"
            class="w-7 h-7 rounded border border-default hover:scale-110 transition-transform"
            style={`background-color: ${color}`}
            title={color}
            aria-label={`색상 ${color}`}
            onclick={() => applyColor(color)}></button>
        {/each}
      </div>
    {/if}
  </div>

  <span class="ml-auto text-caption text-content-tertiary hidden md:inline">
    Markdown 지원
  </span>
</div>

<style>
  .tb-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    font-size: 14px;
    color: var(--color-content-secondary, #4b5563);
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color .15s, border-color .15s;
  }
  .tb-btn:hover {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.1);
  }
  .tb-sep {
    display: inline-block;
    width: 1px;
    height: 20px;
    background: rgba(0, 0, 0, 0.1);
    margin: 0 4px;
  }
</style>
